import React from 'react';
import { Folder, MoreVertical, Edit, Trash2, FolderPlus } from 'lucide-react';
import { Folder as FolderType } from '../../types';
import { useClickOutside } from '../../hooks/useClickOutside';

interface FolderGridProps {
  folders: FolderType[];
  onFolderClick: (folderId: string) => void;
  onEditFolder: (folder: FolderType) => void;
  onDeleteFolder: (folderId: string) => void;
  onCreateSubfolder: (parentFolder: FolderType) => void;
  onDropPrompt?: (folderId: string, promptId: string) => void;
  draggedPromptId?: string | null;
}

export const FolderGrid: React.FC<FolderGridProps> = ({
  folders,
  onFolderClick,
  onEditFolder,
  onDeleteFolder,
  onCreateSubfolder,
  onDropPrompt,
  draggedPromptId,
}) => {
  const [openMenuId, setOpenMenuId] = React.useState<string | null>(null);
  const [dragOverFolderId, setDragOverFolderId] = React.useState<string | null>(null);
  const [menuPosition, setMenuPosition] = React.useState<{ x: number; y: number } | null>(null);
  const [menuTriggerRef, setMenuTriggerRef] = React.useState<HTMLElement | null>(null);

  const menuRef = useClickOutside(() => {
    setOpenMenuId(null);
    setMenuPosition(null);
    setMenuTriggerRef(null);
  });

  const handleMenuClick = (e: React.MouseEvent, folderId: string) => {
    e.stopPropagation();
    
    if (openMenuId === folderId) {
      // Close menu if already open
      setOpenMenuId(null);
      setMenuPosition(null);
      setMenuTriggerRef(null);
      return;
    }
    
    // Calculate absolute position for the dropdown
    const rect = e.currentTarget.getBoundingClientRect();
    const scrollX = window.scrollX || document.documentElement.scrollLeft;
    const scrollY = window.scrollY || document.documentElement.scrollTop;
    
    // Position dropdown below and to the right of the trigger button
    const x = rect.right + scrollX - 160; // 160px = min-width of dropdown
    const y = rect.bottom + scrollY + 4; // 4px gap
    
    setMenuPosition({ x, y });
    setMenuTriggerRef(e.currentTarget as HTMLElement);
    setOpenMenuId(folderId);
  };

  const handleEditClick = (e: React.MouseEvent, folder: FolderType) => {
    e.stopPropagation();
    onEditFolder(folder);
    setOpenMenuId(null);
    setMenuPosition(null);
    setMenuTriggerRef(null);
  };

  const handleDeleteClick = (e: React.MouseEvent, folderId: string) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this folder? All prompts in this folder will be moved to the root level.')) {
      onDeleteFolder(folderId);
    }
    setOpenMenuId(null);
    setMenuPosition(null);
    setMenuTriggerRef(null);
  };

  const handleCreateSubfolderClick = (e: React.MouseEvent, folder: FolderType) => {
    e.stopPropagation();
    onCreateSubfolder(folder);
    setOpenMenuId(null);
    setMenuPosition(null);
    setMenuTriggerRef(null);
  };

  const handleDragOver = (e: React.DragEvent, folderId: string) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    setDragOverFolderId(folderId);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Only clear if we're leaving the target element and not entering a child
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setDragOverFolderId(null);
    }
  };

  const handleDrop = (e: React.DragEvent, folderId: string) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverFolderId(null);
    
    try {
      const dataText = e.dataTransfer.getData('text/plain');
      if (!dataText) {
        console.warn('No drag data found');
        return;
      }
      
      const data = JSON.parse(dataText);
      if (data.type === 'prompt' && data.promptId && onDropPrompt) {
        onDropPrompt(folderId, data.promptId);
        console.log(`Dropped prompt ${data.promptTitle} onto folder`);
      }
    } catch (error) {
      console.error('Error parsing drag data:', error);
    }
  };
  if (folders.length === 0) {
    return null;
  }

  return (
    <>
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4 mb-6">
      {folders.map((folder) => (
        <div
          key={folder.id}
          onClick={() => onFolderClick(folder.id)}
          onDragOver={(e) => handleDragOver(e, folder.id)}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, folder.id)}
          className={`relative group bg-white/98 dark:bg-dark-surface backdrop-blur-sm rounded-xl 
            border p-4 hover:shadow-lg shadow-sm dark:shadow-dark-card
            hover:shadow-fire-card dark:hover:shadow-dark-elevated transition-all duration-300 cursor-pointer 
            transform hover:scale-[1.02] min-h-[100px] flex flex-col items-center justify-center
            ${dragOverFolderId === folder.id 
              ? 'border-fire-500 dark:border-fire-400 bg-fire-50 dark:bg-fire-900/20 border-2 scale-105 shadow-fire-glow' 
              : 'border-gray-200/60 dark:border-dark-border hover:border-fire-300 dark:hover:border-fire-500'
            }
            ${draggedPromptId ? 'ring-2 ring-fire-200 dark:ring-fire-800 border-fire-300 dark:border-fire-600' : ''}`}
        >
          {/* Drop zone indicator */}
          {dragOverFolderId === folder.id && (
            <div className="absolute inset-0 rounded-xl bg-fire-500/10 dark:bg-fire-400/10 border-2 border-dashed border-fire-500 dark:border-fire-400 flex items-center justify-center">
              <div className="bg-fire-500 dark:bg-fire-400 text-white px-3 py-1 rounded-full text-xs font-medium
                shadow-lg animate-pulse">
                Drop here
              </div>
            </div>
          )}

          <div className="relative">
            <Folder
              className={`h-12 w-12 mb-2 transition-all duration-300 ${
                dragOverFolderId === folder.id ? 'scale-110' : ''
              }`}
              style={{ color: folder.color }}
              fill="currentColor"
            />
            
            {/* Menu Button */}
            <button
              onClick={(e) => handleMenuClick(e, folder.id)}
              className="absolute -top-2 -right-2 p-1 opacity-0 group-hover:opacity-100 
                bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-border 
                rounded-full shadow-sm hover:shadow-md transition-all duration-200"
            >
              <MoreVertical className="h-3 w-3 text-gray-600 dark:text-gray-400" />
            </button>
          </div>

          <h3 className="text-sm font-medium text-gray-900 dark:text-dark-text text-center 
            line-clamp-2 leading-tight">
            {folder.name}
          </h3>
        </div>
      ))}
    </div>

      {/* Portal-like Dropdown Menu - Rendered at top level */}
      {openMenuId && menuPosition && (
        <div
          ref={menuRef}
          className="fixed bg-white/98 dark:bg-dark-surface/98 backdrop-blur-md rounded-xl 
            shadow-2xl dark:shadow-dark-elevated border border-gray-200/80 dark:border-dark-border/80 
            py-3 z-[9999] min-w-[180px] transition-all duration-200 ease-out"
          style={{
            left: `${menuPosition.x}px`,
            top: `${menuPosition.y}px`,
          }}
        >
          {(() => {
            const folder = folders.find(f => f.id === openMenuId);
            if (!folder) return null;
            
            return (
              <>
                <button
                  onClick={(e) => handleCreateSubfolderClick(e, folder)}
                  className="w-full text-left px-4 py-3 text-sm text-gray-700 dark:text-gray-300 
                    hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:text-blue-700 dark:hover:text-blue-300
                    transition-all duration-200 flex items-center font-medium"
                >
                  <FolderPlus className="h-4 w-4 mr-3 text-blue-500 dark:text-blue-400" />
                  New Subfolder
                </button>
                <button
                  onClick={(e) => handleEditClick(e, folder)}
                  className="w-full text-left px-4 py-3 text-sm text-gray-700 dark:text-gray-300 
                    hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200 flex items-center font-medium"
                >
                  <Edit className="h-4 w-4 mr-3 text-gray-500 dark:text-gray-400" />
                  Edit Folder
                </button>
                <hr className="my-1 border-gray-200 dark:border-gray-600" />
                <button
                  onClick={(e) => handleDeleteClick(e, folder.id)}
                  className="w-full text-left px-4 py-3 text-sm text-red-600 dark:text-red-400 
                    hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-700 dark:hover:text-red-300
                    transition-all duration-200 flex items-center font-medium"
                >
                  <Trash2 className="h-4 w-4 mr-3" />
                  Delete Folder
                </button>
              </>
            );
          })()}
        </div>
      )}
    </>
  );
};